import { useMutation } from '@tanstack/react-query'
import { ChipInput } from '@/components/ChipInput'
import { InfoTooltip } from '@/components/InfoTooltip'
import { RequiredKeywordGroups } from '@/components/RequiredKeywordGroups'
import { testIdentification } from '@/api/testIdentification'
import { errorMessage } from '@/lib/apiErrors'
import type { DocumentTypeIdentification } from '@/types/bundle'
import { PageNumbersInput } from './PageNumbersInput'
import { TestPanel } from './TestPanel'

export interface IdentificationTabProps {
  documentTypeId: string
  identification: DocumentTypeIdentification
  onChange: (identification: DocumentTypeIdentification) => void
}

export function IdentificationTab({ documentTypeId, identification, onChange }: IdentificationTabProps) {
  // Fired with the current in-memory identification block on every Test
  // click, unconditionally - Frontend PRD 03's core acceptance bar (AC5):
  // Test always reflects unsaved edits, with zero dependency on Save.
  const testMutation = useMutation({
    mutationFn: (pdfBase64: string) =>
      testIdentification({
        documentTypeId,
        pdfBase64,
        identificationOverride: identification,
      }),
  })

  const result = testMutation.data ?? null
  const matchedRequired = result && result.outcome !== 'no_text_layer' ? result.matchedRequired : undefined
  const matchedSupporting =
    result && result.outcome !== 'no_text_layer' ? result.matchedSupporting : undefined
  const groupHit = result && result.outcome !== 'no_text_layer' ? result.groupHit : undefined

  return (
    <div className="flex flex-col gap-6">
      <section className="flex flex-col gap-4 rounded-control border border-border bg-surface p-4">
        <h2 className="flex items-center gap-1 text-header font-semibold text-text-primary">
          Required keywords
          <InfoTooltip text="At least one keyword from every group must be found for a document to be identified as this type. Groups are combined with AND; keywords within a group are combined with OR." />
        </h2>
        <RequiredKeywordGroups
          groups={identification.required_keywords_any_of}
          onChange={(groups) => onChange({ ...identification, required_keywords_any_of: groups })}
          matchedPhrases={matchedRequired}
          groupHit={groupHit}
        />
      </section>

      <section className="flex flex-col gap-4 rounded-control border border-border bg-surface p-4">
        <h2 className="flex items-center gap-1 text-header font-semibold text-text-primary">
          Supporting keywords
          <InfoTooltip text="Extra words that add confidence this is the right document, but aren't required on their own." />
        </h2>
        <ChipInput
          value={identification.supporting_keywords ?? []}
          onChange={(supporting_keywords) => onChange({ ...identification, supporting_keywords })}
          placeholder="Type a phrase, press Enter…"
          reorderable
          highlighted={matchedSupporting}
        />
        <div className="flex flex-col gap-1">
          <label className="flex items-center gap-1 text-meta font-medium text-text-secondary" htmlFor="min-supporting-matches">
            Min supporting matches
            <InfoTooltip text="How many supporting keywords must be found before they count toward identifying this document." />
          </label>
          <input
            id="min-supporting-matches"
            type="number"
            min={0}
            value={identification.min_supporting_matches ?? 0}
            onChange={(event) => {
              const numeric = Number(event.target.value)
              onChange({
                ...identification,
                min_supporting_matches: Number.isNaN(numeric) ? 0 : Math.max(0, numeric),
              })
            }}
            className="w-32 rounded-control border border-border bg-bg px-3 py-2 text-body text-text-primary outline-none"
          />
        </div>
      </section>

      <section className="flex flex-col gap-4 rounded-control border border-border bg-surface p-4">
        <h2 className="flex items-center gap-1 text-header font-semibold text-text-primary">
          Search pages
          <InfoTooltip text="Which pages of an uploaded document to scan for the keywords above. Leave empty to search every page." />
        </h2>
        <PageNumbersInput
          value={identification.search_pages}
          onChange={(search_pages) => onChange({ ...identification, search_pages })}
        />
      </section>

      <section className="flex flex-col gap-4 rounded-control border border-border bg-surface p-4">
        <h2 className="flex items-center gap-1 text-header font-semibold text-text-primary">Matching</h2>
        <label className="flex w-fit items-center gap-2 text-body text-text-primary">
          <input
            type="checkbox"
            checked={identification.case_insensitive ?? true}
            onChange={(event) => onChange({ ...identification, case_insensitive: event.target.checked })}
          />
          Case insensitive matching
          <InfoTooltip text="When on, keyword matching ignores letter case (e.g. 'INVOICE' matches 'invoice'). On by default." />
        </label>
      </section>

      <TestPanel
        onTest={(pdfBase64) => testMutation.mutate(pdfBase64)}
        isPending={testMutation.isPending}
        result={result}
        error={testMutation.isError ? (errorMessage(testMutation.error) ?? 'Test failed') : null}
      />
    </div>
  )
}
